
import * as React from 'react';
import {
    IDetailsListProps,
    IDetailsHeaderProps,
    ConstrainMode,
    DetailsListLayoutMode
} from 'office-ui-fabric-react/lib/DetailsList';
import {
    ISelectionZoneProps
} from 'office-ui-fabric-react/lib/Selection';
import {
    MarqueeSelection
} from 'office-ui-fabric-react/lib/MarqueeSelection';
import {
    IRenderFunction
} from 'office-ui-fabric-react/lib/Utilities';
import {
    withColumnSelector, IDecorateWithColumnSelectorProps
} from '../decorators/withColumnSelector';
import { withCommandColumns, ICommandColumnsProps } from '../decorators/withCommandColumns';
import {
    withCachedScrollPosition, IDecorateWithCachedScrollPositionProps
} from '../decorators/withCachedScrollPosition';
import {
    withItemSetRowsAndGroups, IDecorateWithItemSetRowsAndGroupsProps
} from './decorators/withItemSetRowsAndGroups';
import {
    withLayoutRenderComplete, IDecorateWithLayoutRenderCompleteProps
} from '../decorators/withLayoutRenderComplete';
import { injectProps, wrapWithParent, IReactChildrenProps } from '../../utilities/components/Props';
import strings from './DetailsList.resx';
import { withDetailsListShimmer } from './decorators/withDetailsListShimmer';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { isPageLoaded } from '../../operations/pageLoad';
import { IItemColumn } from '../../models/schema/IItemColumn';
import { areShallowDifferent } from '@ms/utilities-objects/lib/areShallowEqual';
import { getFieldRenderBarrier } from '../fields/FieldHelper';
import { DeferredRender } from '../../utilities/components/DeferredRender';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';
import { createTelemetryProvider } from '../telemetry/TelemetryProvider';
import { BackgroundTaskContextProvider } from '../backgroundTaskRunner/BackgroundTaskHost';
import { PAGE_LOAD_BARRIER_CONTEXT, itemDetailsRowColumnsControl } from './ItemDetailsRow';
import { ControlHandlerProvider } from '../decorators/withControlHandler';
import { getConfiguration } from '../../models/configuration/getConfiguration';
import { disableShimmerBeforePageLoadConfigurationFacet } from '../../facets/ConfigurationFacets';
import { enableItemDetailsRowRenderDebounceFacet } from '../../facets/ConfigurationFacets';
import { ItemCacheSuspense } from '../itemCacheSuspense/ItemCacheSuspense';
import { findElementRecursive } from '@uifabric/utilities/lib/dom/findElementRecursive';

const LIST_DEFAULT_PROPS: Partial<IDetailsListProps> = {
    constrainMode: ConstrainMode.unconstrained,
    // Add default behaviors for the DetailsList here.
    ariaLabelForGrid: strings.listTooltip,
    // These are behaviors supplied by control handlers.
    ariaLabelForSelectionColumn: strings.selectionColumnHeaderAriaLabel,
    ariaLabelForSelectAllCheckbox: strings.toggleSelectAllAriaLabel,
    ariaLabelForListHeader: strings.ColumnHeaderOperationAriaLabel,
    layoutMode: DetailsListLayoutMode.justified,
};

interface IWithPageLoadBarrierProps {
    pageLoadBarrier?: Promise<void>;
}

type IBlankOutColumnsBeforePageLoadProps = Pick<ICommandColumnsProps, 'columns'>;

function blankOutColumnsBeforePageLoad(
    itemCache: IItemCache,
    props: IBlankOutColumnsBeforePageLoadProps
): IBlankOutColumnsBeforePageLoadProps & IWithPageLoadBarrierProps {
    const pageLoadBarrier = getFieldRenderBarrier(itemCache);
    const pageLoaded = isPageLoaded(itemCache);

    function removeContentFromColumnHeader(column: IItemColumn): IItemColumn {
        const {
            name,
            iconName,
            iconClassName,
            headerCommand,
            onColumnClick,
            onColumnContextMenu,
            columnActionsMode,
            ...newColumn
        } = column;

        return {
            ...newColumn,
            // Columns still need a name field.
            name: ''
        };
    }

    const columns = !pageLoadBarrier && !pageLoaded && props.columns ?
        // If the page has not loaded but there are columns,
        // trim all the 'visual' data off of them.
        props.columns.map(removeContentFromColumnHeader) :
        props.columns;

    return {
        columns,
        pageLoadBarrier
    };
}

type IDeferRenderDetailsHeaderProps = IDetailsHeaderProps & {
    defaultRender: IRenderFunction<IDetailsHeaderProps>;
} & IWithPageLoadBarrierProps;

function DeferRenderDetailsHeader(props: IDeferRenderDetailsHeaderProps): JSX.Element {
    const {
        defaultRender,
        pageLoadBarrier,
        ...detailsHeaderProps
    } = props;

    return (
        <DeferredRender
            barrier={ pageLoadBarrier }
            placeholder={ <div style={ { height: '35px' } } /> }
        >
            {
                defaultRender(detailsHeaderProps)
            }
        </DeferredRender>
    );
}

// Bind the commands specified in the columns to context menus.
const WithCommandColumnsDetailsHeader = connectToItemCache(blankOutColumnsBeforePageLoad)(
    withCommandColumns()(DeferRenderDetailsHeader)
);

const renderDetailsHeader = (
    props?: IDetailsHeaderProps,
    defaultRender?: IRenderFunction<IDetailsHeaderProps>
): JSX.Element | null => {
    return props && defaultRender ? (
        <WithCommandColumnsDetailsHeader
            { ...props }
            defaultRender={ defaultRender }
        />
    ) : null;
};

type IInjectableDetailsListProps = IDetailsListProps & {
    detailsListAs: React.ComponentType<IDetailsListProps>;
};

function InjectableDetailsList(props: IInjectableDetailsListProps): JSX.Element {
    const {
        detailsListAs: DetailsList,
        ...detailsListProps
    } = props;

    return (
        <DetailsList { ...detailsListProps } />
    );
}

const DETAILS_ROW_TELEMETRY_CONTEXT: IEventContext = {
    component: 'DetailsRow'
};

const DetailsRowTelemetryProvider = createTelemetryProvider({
    withContext: DETAILS_ROW_TELEMETRY_CONTEXT
});

type IDetailsListWrapperProps = Pick<IDetailsListProps, 'selection' | 'columns'> & {
    disableMarqueeSelection?: boolean;
    enableItemDetailsRowRenderDebounce?: boolean;
} & IWithPageLoadBarrierProps & IReactChildrenProps;

function DetailsListWrapper(props: IDetailsListWrapperProps): JSX.Element {
    const {
        selection,
        disableMarqueeSelection,
        enableItemDetailsRowRenderDebounce,
        columns,
        children
    } = props;

    const controlHandlers = React.useMemo(() => {
        return itemDetailsRowColumnsControl.pack(() => {
            return {
                columns
            };
        });
    }, [columns]);

    const marqueeSelectionDetailsList = selection && !disableMarqueeSelection ? (
        <MarqueeSelection selection={ selection } onShouldStartSelection={ onShouldStartSelection }>
            { children }
        </MarqueeSelection>
    ) : children;

    const suspenseDetailsList = enableItemDetailsRowRenderDebounce ? (
        <ItemCacheSuspense mode='debounce'>
            { marqueeSelectionDetailsList }
        </ItemCacheSuspense>
    ) : marqueeSelectionDetailsList;

    return (
        <DetailsRowTelemetryProvider>
            <BackgroundTaskContextProvider scope='local'>
                <ControlHandlerProvider controlHandlers={ controlHandlers }>
                    <PAGE_LOAD_BARRIER_CONTEXT.Provider value={ props.pageLoadBarrier }>
                        { suspenseDetailsList }
                    </PAGE_LOAD_BARRIER_CONTEXT.Provider>
                </ControlHandlerProvider>
            </BackgroundTaskContextProvider>
        </DetailsRowTelemetryProvider>
    );
}

const DetailsListShimmerAfterPageLoad = connectToItemCache((
    itemCache: IItemCache,
    props: Pick<IDetailsListProps, 'enableShimmer'>
): Pick<IDetailsListProps, 'enableShimmer'> => {
    const {
        disableShimmerBeforePageLoadConfiguration = true // TODO Set this to false after consumers have been updated.
    } = getConfiguration(itemCache, {
        disableShimmerBeforePageLoadConfiguration: disableShimmerBeforePageLoadConfigurationFacet
    });

    if (!disableShimmerBeforePageLoadConfiguration) {
        return {};
    }

    const pageLoaded = isPageLoaded(itemCache);

    return {
        // to help perf, disable shimmer before PLT
        enableShimmer: props.enableShimmer && pageLoaded
    };
})(InjectableDetailsList);

/**
 * Wrap the details list with marquee selection.
 */
const DetailsListWithWrappers = wrapWithParent(DetailsListWrapper)(DetailsListShimmerAfterPageLoad);

/**
 * Add accessibility and render cleanup to the DetailsList.
 */
const DetailsListWithDefaultProps = injectProps(() => {
    let previousSelectionZoneProps: ISelectionZoneProps | undefined;

    type ISelectionZoneOverrideProps = Pick<IDetailsListProps, 'selection' | 'selectionZoneProps'>;

    function getSelectionZoneOverrides(props: ISelectionZoneOverrideProps): ISelectionZoneOverrideProps {
        const {
            selection,
            selectionZoneProps: originalSelectionZoneProps
        } = props;

        const selectionZoneProps = originalSelectionZoneProps || selection ? {
            ...(originalSelectionZoneProps || {}),
            ...(selection ? {
                selection,
                disableAutoSelectOnInputElements: true
            } : {})
        } as ISelectionZoneProps : undefined;

        const finalSelectionZoneProps =
            !previousSelectionZoneProps || areShallowDifferent(previousSelectionZoneProps, selectionZoneProps) ?
                selectionZoneProps :
                previousSelectionZoneProps;

        previousSelectionZoneProps = finalSelectionZoneProps;

        return {
            selection,
            selectionZoneProps: finalSelectionZoneProps
        };
    }

    return (props: IDetailsListProps): Partial<IDetailsListProps> => {
        return {
            ...LIST_DEFAULT_PROPS,
            // Pass the original props, allow whatever is present as an override for the above 'defaults'.
            ...props,
            ...getSelectionZoneOverrides(props)
        };
    };
})(DetailsListWithWrappers);

function composeRenderFunctions<TProps>(outer: IRenderFunction<TProps>, inner: IRenderFunction<TProps>): IRenderFunction<TProps> {
    // TODO Memoize this and move this to Fabric.
    function composedDefaultRender(
        props?: TProps,
        defaultRender?: IRenderFunction<TProps>
    ): JSX.Element | null {
        return outer(props, (innerProps?: TProps) => {
            return inner(innerProps, defaultRender);
        });
    }

    return composedDefaultRender;
}

/**
 * Bubble up an event for each layout re-render.
 */
const LayoutRenderDetailsList = withLayoutRenderComplete({
    qosName: 'Details'
})(DetailsListWithDefaultProps);

/**
 * Remember the last-saved scroll position for the current item.
 */
const ScrollPositionDetailsList = withCachedScrollPosition()(LayoutRenderDetailsList);

type IItemSetDetailsListInjectedProps = Pick<IDetailsListProps,
    'initialFocusedIndex' |
    'onRenderDetailsHeader'>;

/**
 * Show a context menu when column headers are clicked.
 */
const CommandHeaderDetailsList = connectToItemCache((
    itemCache: IItemCache,
    props: Pick<IDetailsListProps, 'onRenderDetailsHeader'>
): IItemSetDetailsListInjectedProps & IWithPageLoadBarrierProps & Pick<IDetailsListWrapperProps, 'enableItemDetailsRowRenderDebounce'> => {
    const pageLoadBarrier = getFieldRenderBarrier(itemCache);

    const {
        enableItemDetailsRowRenderDebounce
    } = getConfiguration(itemCache, {
        enableItemDetailsRowRenderDebounce: enableItemDetailsRowRenderDebounceFacet
    });

    const {
        onRenderDetailsHeader
    } = props;

    return {
        onRenderDetailsHeader: onRenderDetailsHeader ?
            composeRenderFunctions(onRenderDetailsHeader, renderDetailsHeader) :
            renderDetailsHeader,
        pageLoadBarrier,
        enableItemDetailsRowRenderDebounce
    };
})(ScrollPositionDetailsList);

/**
 * Add Shimmer around a standard DetailsList.
 */
const ShimmeredDetailsList = withDetailsListShimmer()(CommandHeaderDetailsList);

/**
 * Bind the rows and groups in the details list to an item set.
 */
const RowsAndGroupsDetailsList = withItemSetRowsAndGroups()(ShimmeredDetailsList);

export type IItemSetDetailsListProps = IDecorateWithColumnSelectorProps<
    IDecorateWithItemSetRowsAndGroupsProps<
        IDecorateWithLayoutRenderCompleteProps<
            IDecorateWithCachedScrollPositionProps<
                IDetailsListProps
            >
        >
    >
>;

/**
 * Component which binds the items, pages, and groups within an item set (for a given itemKey)
 * to the rows and groups of a Fabric DetailsList.
 *
 * This component does not provide default columns, renderers, or selection behavior other
 * than the default from Fabric. Use additional decorators to implement full scenarios.
 */

/**
 * Add support for a 'columnSelector' prop in case the caller needs to assign columns dynamically.
 */
export const ItemSetDetailsList: React.ComponentType<IItemSetDetailsListProps> = withColumnSelector()(RowsAndGroupsDetailsList);

ItemSetDetailsList.displayName = 'ItemSetDetailsList';

function onShouldStartSelection(event: MouseEvent): boolean {
    return !findElementRecursive(event.target as HTMLElement | null, (element: HTMLElement) => {
        return element.getAttribute('data-selection-index') !== null;
    });
}
